import React from "react";

import { faExclamationTriangle, faWifiSlash } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import bowser from "bowser";

import history from "~/lib/history";
import { globalFetchKeyContext } from "~/lib/relayUtil";
import { CtrlOrCmd, isDesktopApp, isTouchScreen, UpOrOption } from "~/lib/utils";
import Button from "~/ui/button";

import styles from "./FullPageError.module.css";

interface FullPageErrorProps {
	message: string;
}

export function ErrorRecoveryInstructions() {
	const goBack = React.useCallback(() => {
		history.go(-1);
	}, []);

	const goHome = React.useCallback(() => {
		window.location.pathname = "/";
	}, []);

	const reload = React.useCallback(() => {
		window.location.reload();
	}, []);

	const keyCombination =
		bowser.getParser(window.navigator.userAgent).is("macOS") || isDesktopApp ? (
			<>
				<kbd>{CtrlOrCmd}</kbd> + <kbd>{UpOrOption}</kbd> + <kbd>R</kbd>
			</>
		) : (
			<>
				<kbd>{CtrlOrCmd}</kbd> + <kbd>F5</kbd>
			</>
		);

	return isTouchScreen ? (
		<div className={styles.oopsButtonContainer}>
			<Button onClick={goBack}>Go Back</Button>
			<Button onClick={goHome}>Go Home</Button>
			<Button onClick={reload}>Reload</Button>
		</div>
	) : (
		<p>You can try reloading this page with the keyboard shortcut {keyCombination}.</p>
	);
}

export default function FullPageError({ message }: FullPageErrorProps) {
	return (
		<div className={styles.oopsPage}>
			<FontAwesomeIcon icon={faExclamationTriangle} color="var(--errorTextColor)" size="7x" />
			<h1 className={styles.oopsMessage}>{message}</h1>
			<ErrorRecoveryInstructions />
		</div>
	);
}

export function FullPageOffline({ retry }: { retry: () => void }) {
	const setGlobalFetchKey = globalFetchKeyContext.useSetter();
	const updateGlobalFetchKeyAndRetry = React.useCallback(() => {
		setGlobalFetchKey(n => n + 1);
		retry();
	}, [setGlobalFetchKey, retry]);

	React.useEffect(() => {
		// If we go online, retry
		window.addEventListener("online", updateGlobalFetchKeyAndRetry);
		return () => window.removeEventListener("online", updateGlobalFetchKeyAndRetry);
	}, [updateGlobalFetchKeyAndRetry]);

	return (
		<div className={styles.oopsPage}>
			<FontAwesomeIcon icon={faWifiSlash} size="7x" />
			<h1 className={styles.oopsMessage}>No internet connection</h1>
			<div className={styles.oopsButtonContainer}>
				<Button onClick={updateGlobalFetchKeyAndRetry}>Retry</Button>
			</div>
		</div>
	);
}
